import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINTS } from '@/api/endpoints';
import { fetchClient } from '@/api/fetch-client';
import {
  ComparisonFindingsError,
  ComparisonFindingsRequest,
  ComparisonFindingsResponse,
  ComparisonReportPdfError,
  ComparisonReportPdfRequest,
  ComparisonReportPdfResponse,
  PriorStudiesError,
  PriorStudiesRequest,
  PriorStudiesResponse,
} from '@/api/model';
import { removeEmptyParams } from '@/api/utils';

const KEY = {
  GET_COMPARISON_FINDINGS: 'GET_COMPARISON_FINDINGS',
  GET_COMPARISON_REPORT_PDF: 'GET_COMPARISON_REPORT_PDF',
  GET_PRIOR_STUDIES: 'GET_PRIOR_STUDIES',
} as const;

const request = {
  getComparisonFindings: async (
    { payload: { params, priorStudyId } }: ComparisonFindingsRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await fetchClient.FCT.get<ComparisonFindingsResponse>(
      `${ENDPOINTS.FCT.COMPARISON.PRIOR_STUDY}${priorStudyId}/findings`,
      {
        params: removeEmptyParams(params ?? {}),
        signal,
      },
    );

    return data;
  },
  getComparisonReportPdf: async (
    { payload: { params } }: ComparisonReportPdfRequest,
    signal?: AbortSignal,
  ) => {
    const { studyId } = params;
    const { data } = await fetchClient.FCT.get<ComparisonReportPdfResponse>(
      `${ENDPOINTS.FCT.COMPARISON.REPORT_PDF}${studyId}`,
      { signal },
    );

    return data;
  },
  getPriorStudies: async ({ payload: { params } }: PriorStudiesRequest, signal?: AbortSignal) => {
    const { data } = await fetchClient.FCT.get<PriorStudiesResponse>(
      ENDPOINTS.FCT.COMPARISON.PRIOR_STUDIES,
      {
        params: removeEmptyParams(params),
        signal,
      },
    );

    return data;
  },
};

const query = {
  useGetComparisonFindings: (
    { payload }: ComparisonFindingsRequest,
    config?: Partial<UseQueryOptions<ComparisonFindingsResponse, ComparisonFindingsError>>,
  ) =>
    useQuery<ComparisonFindingsResponse, ComparisonFindingsError>({
      enabled: !!payload.priorStudyId,
      queryFn: ({ signal }) => request.getComparisonFindings({ payload }, signal),
      queryKey: [KEY.GET_COMPARISON_FINDINGS, payload.priorStudyId, payload.params],
      ...config,
    }),
  useGetComparisonReportPdf: (
    { payload }: ComparisonReportPdfRequest,
    config?: Partial<UseQueryOptions<ComparisonReportPdfResponse, ComparisonReportPdfError>>,
  ) =>
    useQuery<ComparisonReportPdfResponse, ComparisonReportPdfError>({
      enabled: !!payload.params.studyId,
      queryFn: ({ signal }) => request.getComparisonReportPdf({ payload }, signal),
      queryKey: [KEY.GET_COMPARISON_REPORT_PDF, payload.params.studyId],
      ...config,
    }),
  useGetPriorStudies: (
    { payload }: PriorStudiesRequest,
    config?: Partial<UseQueryOptions<PriorStudiesResponse, PriorStudiesError>>,
  ) =>
    useQuery<PriorStudiesResponse, PriorStudiesError>({
      enabled: !!payload.params.patientId && !!payload.params.currentStudyId,
      queryFn: ({ signal }) => request.getPriorStudies({ payload }, signal),
      queryKey: [KEY.GET_PRIOR_STUDIES, payload.params.patientId, payload.params.currentStudyId],
      ...config,
    }),
};

export const comparison = {
  KEY,
  query,
  request,
};
